import {Button, Dialog, DialogBackdrop, DialogPanel, DialogTitle, Input, Select} from "@headlessui/react";
import {useEffect, useState} from "react";
import {
    ArrowPathIcon,
    DocumentArrowDownIcon,
    MagnifyingGlassIcon,
    PlusCircleIcon,
    PlusIcon,
    TrashIcon
} from "@heroicons/react/24/outline";
import {db} from "../../firebase/firebase";
import {useAuth} from "../../contexts/AuthProvider";
import { format } from 'date-fns'; // if not installed, run: npm install date-fns
import * as XLSX from "xlsx";
import {numberWithCommas} from "../GlobalFunctions";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const ProductsPage = () => {

    const { currentUser } = useAuth();

    const [open, setOpen] = useState(false);

    const [name, setName] = useState('');
    const [unitPrice, setUnitPrice] = useState('');
    const [quantity, setQuantity] = useState('');
    const [category, setCategory] = useState('');
    const [barcode, setBarcode] = useState('');

    const [newRow, setNewRow] = useState({
        name: "",
        unitPrice: "",
        quantity: "",
        category: "",
    });
    const inlineRefs = {
        name: useState(null)[0],
        price: useState(null)[0],
        qty: useState(null)[0],
        cat: useState(null)[0],
    };

    const lastUpdated = localStorage.getItem(`inventory_last_updated_${currentUser.uid}`);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const collectionRef = db.collection('Inventory');

        const parsedFormData = {
            quantity: parseFloat(quantity),
            name: name,
            unitPrice: parseFloat(unitPrice),
            category: category,
            barcode: barcode,
        };

        try {
            // Add the new document to Firestore
            await collectionRef.add(parsedFormData);

            console.log('Item added successfully!');
            // await onUpdate();


            setQuantity("");
            setCategory("");
            setName("");
            setUnitPrice("");

            setOpen(false);

            // clearForm(); // Clear the form after submission
        } catch (error) {
            console.error('Error adding/item: ', error);
        }
    };

    const [inventory, setInventory] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchInventory = async () => {
            if (currentUser) {
                try {
                    const storedInventoryData = localStorage.getItem(`inventory_${currentUser.uid}`);
                    if (storedInventoryData) {
                        const inventoryData = JSON.parse(storedInventoryData);
                        setInventory(inventoryData);
                    } else {
                        const inventoryCollectionRef = db.collection('Inventory');
                        const snapshot = await inventoryCollectionRef.get();

                        if (!snapshot.empty) {
                            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                            setInventory(inventoryData);
                            localStorage.setItem(`inventory_${currentUser.uid}`, JSON.stringify(inventoryData));
                        } else {
                            console.log('No inventory found');
                        }
                    }
                } catch (error) {
                    console.error('Error fetching inventory:', error);
                }
            }
        };

        fetchInventory();
    }, [currentUser]);

    const onUpdate = async () => {
        const inventoryCollectionRef = db.collection('Inventory');
        const snapshot = await inventoryCollectionRef.get();

        if (!snapshot.empty) {
            const inventoryData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setInventory(inventoryData);
            localStorage.setItem(`inventory_${currentUser.uid}`, JSON.stringify(inventoryData));


            // Format and save last updated timestamp
            const now = new Date();
            const formatted = format(now, 'MMM dd, yyyy \'at\' hh:mm a');
            localStorage.setItem(`inventory_last_updated_${currentUser.uid}`, formatted);

        } else {
            console.log('No inventory found');
        }
    };

    const deleteItem = async (id) => {
        try {
            await db.collection('Inventory').doc(id).delete();

            await onUpdate();

            console.log(`Item with id ${id} deleted`);
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    const saveItem = async (item) => {
        try {
            await db.collection('Inventory').doc(item.id).update({
                name: item.name,
                unitPrice: item.unitPrice,
                quantity: item.quantity,
            });

            await onUpdate();

            console.log('Item updated successfully');
        } catch (error) {
            console.error('Error updating item:', error);
        }
    };

    const addInlineItem = async () => {
        try {
            if (!newRow.name) return;

            const payload = {
                name: newRow.name,
                unitPrice: parseFloat(newRow.unitPrice) || 0,
                quantity: parseFloat(newRow.quantity) || 0,
                category: newRow.category || "",
            };

            await db.collection('Inventory').add(payload);

            setNewRow({
                name: "",
                unitPrice: "",
                quantity: "",
                category: "",
            });

            await onUpdate();
        } catch (err) {
            console.error("Error adding inline item:", err);
        }
    };

    const handleInlineKey = (e, field) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (field === "name") inlineRefs.price?.focus();
            if (field === "price") inlineRefs.qty?.focus();
            if (field === "qty") inlineRefs.cat?.focus();
            if (field === "cat") addInlineItem();
        }
    };

    const handlePaste = (e) => {
        const text = e.clipboardData.getData("text");
        if (!text) return;

        const rows = text.split("\n").map(r => r.trim()).filter(Boolean);

        const parsed = rows.map(row => {
            const [name, unitPrice, quantity, category] = row.split("\t");
            return {
                name,
                unitPrice: parseFloat(unitPrice) || 0,
                quantity: parseFloat(quantity) || 0,
                category: category || "",
            };
        });

        const upload = async () => {
            for (let item of parsed) {
                await db.collection("Inventory").add(item);
            }
            await onUpdate();
        };

        upload();
    };


    // Export to Excel function
    const exportToExcel = () => {
        try {
            if (!inventory || inventory.length === 0) return;

            // format data
            const data = inventory.map(item => ({
                Name: item.name,
                SKU: item.id,
                UnitPrice: item.unitPrice,
                Quantity: item.quantity,
                Category: item.category,
                Total: (item.unitPrice || 0) * (item.quantity || 0),
            }));

            // create worksheet
            const worksheet = XLSX.utils.json_to_sheet(data);

            // create workbook
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Inventory");

            // export file
            XLSX.writeFile(workbook, "inventory.xlsx");

            console.log("Excel export successful");
        } catch (error) {
            console.error("Error exporting Excel:", error);
        }
    };

    const filteredInventory = inventory.filter((item) => {
        const term = searchTerm.toLowerCase();

        return (
            item.name?.toLowerCase().includes(term) ||
            item.category?.toLowerCase().includes(term) ||
            item.id?.toLowerCase().includes(term)
        );
    });

    return (
        <div className="">

            <div className="p-5 space-y-2 flex flex-col bg-white h-dvh">

                {/* Search + Actions */}
                <div className="pb-4  grid md:grid-cols-2 gap-3 border-b border-line-2">

                    {/* Search */}
                    <div className="relative">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search products"
                            className="w-full py-2 ps-10 pe-3 bg-surface border border-surface-line rounded-lg text-sm"
                        />
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground-1">
                            <MagnifyingGlassIcon className={`size-4`}/>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end items-center gap-2">

                        <Button
                            onClick={exportToExcel}
                            className="py-2 px-3 flex items-center gap-1.5 hover:bg-opacity-50 text-xs rounded-lg bg-blue-600 text-white"
                        >
                            <DocumentArrowDownIcon className={`size-4`}/>
                            Export Excel
                        </Button>

                        <Button
                            onClick={onUpdate}
                            className="py-2 px-3 flex items-center gap-1.5 hover:bg-opacity-50 text-xs rounded-lg bg-blue-600 text-white"
                        >
                            <ArrowPathIcon className={`size-4`} />
                            Refresh
                        </Button>

                        {/*/!* Filter *!/*/}
                        {/*<Button*/}
                        {/*    onClick={() => setOpen(true)}*/}
                        {/*    className="py-2 px-3 flex items-center gap-1.5 hover:bg-opacity-50 text-xs rounded-lg bg-blue-600 text-white"*/}
                        {/*>*/}
                        {/*    <PlusIcon className={`size-4`}/>*/}
                        {/*    Add Item*/}
                        {/*</Button>*/}
                    </div>

                </div>

                {/* Table */}
                <div className="overflow-x-auto">

                    <table className="min-w-full divide-y divide-table-line text-sm">
                        <thead>
                        <tr className="text-left text-muted-foreground-1">
                            <th className="p-3">Item</th>
                            <th className="p-3">SKU</th>
                            <th className="p-3">Unit Price</th>
                            <th className="p-3">Stock</th>
                            <th className="p-3">Category</th>
                            <th className="p-3">Total</th>
                            <th className="p-3"></th>
                        </tr>
                        </thead>

                        <tbody className="divide-y divide-table-line">
                        <tr className="bg-gray-50">
                            <td className="p-3">
                                <input
                                    ref={(el) => (inlineRefs.name = el)}
                                    value={newRow.name}
                                    onPaste={handlePaste}
                                    onKeyDown={(e) => handleInlineKey(e, "name")}
                                    onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
                                    placeholder="Item name..."
                                    className="w-full bg-transparent border-b border-gray-300 focus:outline-none"
                                />
                            </td>

                            <td className="p-3 text-gray-400 text-xs">
                                New
                            </td>

                            <td className="p-3">
                                <input
                                    ref={(el) => (inlineRefs.price = el)}
                                    type="number"
                                    value={newRow.unitPrice}
                                    onKeyDown={(e) => handleInlineKey(e, "price")}
                                    onChange={(e) => setNewRow({ ...newRow, unitPrice: e.target.value })}
                                    placeholder="0.00"
                                    className="w-full bg-transparent border-b border-gray-300 focus:outline-none"
                                />
                            </td>

                            <td className="p-3">
                                <input
                                    ref={(el) => (inlineRefs.qty = el)}
                                    type="number"
                                    value={newRow.quantity}
                                    onKeyDown={(e) => handleInlineKey(e, "qty")}
                                    onChange={(e) => setNewRow({ ...newRow, quantity: e.target.value })}
                                    placeholder="0"
                                    className="w-full bg-transparent border-b border-gray-300 focus:outline-none"
                                />
                            </td>

                            <td className="p-3">
                                <Select
                                    ref={(el) => (inlineRefs.cat = el)}
                                    value={newRow.category}
                                    onKeyDown={(e) => handleInlineKey(e, "cat")}
                                    onChange={(e) => setNewRow({ ...newRow, category: e.target.value })}
                                    className="w-full bg-transparent border-b border-gray-300 focus:outline-none"
                                >
                                    <option value="">Select Category...</option>
                                    <option value="Clothing">Clothing</option>
                                    <option value="Shoes">Shoes</option>
                                    <option value="Baby">Baby</option>
                                    <option value="Bedding">Bedding</option>
                                    <option value="Misc">Misc.</option>
                                </Select>
                            </td>

                            <td className="p-3 text-gray-400 text-xs">
                                $ {numberWithCommas(newRow.unitPrice * newRow.quantity)} USD
                            </td>

                            <td className="p-2 flex justify-end">
                                <Button
                                    onClick={addInlineItem}
                                    className="bg-blue-600 hover:bg-opacity-50 flex items-center gap-2 text-white px-3 py-1.5 text-xs rounded-md"
                                >
                                    <PlusIcon className={`size-4`}/>
                                    Add Item
                                </Button>
                            </td>
                        </tr>
                        {filteredInventory.length === 0 && !searchTerm ? (
                            <tr>
                                <td colSpan={7} className="py-12 text-center text-sm text-gray-500">
                                    <div className="flex flex-col items-center justify-center gap-3">
                                        <MagnifyingGlassIcon className="size-8 text-gray-300" />
                                        <p className="font-medium text-gray-700">
                                            {searchTerm ? "No results found" : "No inventory yet"}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            {searchTerm
                                                ? "Try adjusting your search or filters"
                                                : "Add your first item to get started"}
                                        </p>

                                        {!searchTerm && (
                                            <Button
                                                onClick={() => setOpen(true)}
                                                className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-opacity-80"
                                            >
                                                <PlusIcon className="size-4" />
                                                Add Item
                                            </Button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredInventory.map((item, planIdx) => (
                                <tr key={item.id}>
                                    <td
                                        className={classNames(
                                            planIdx === 0 ? '' : 'border-t border-transparent',
                                            'relative',
                                        )}
                                    >
                                        <input
                                            value={item.name}
                                            onChange={(e) => {
                                                const updated = [...inventory];
                                                updated[planIdx].name = e.target.value;
                                                setInventory(updated);
                                            }}
                                            className="font-medium text-gray-900 bg-transparent border-b border-gray-200 focus:outline-none w-full"
                                        />
                                        <div className="mt-1 flex flex-col text-gray-500 sm:block lg:hidden">
                                            <span>
                                              ${item.unitPrice} / {item.quantity}
                                            </span>
                                            <span className="hidden sm:inline">·</span>
                                            <span>{item.category}</span>
                                        </div>
                                        {planIdx !== 0 ? <div className="absolute -top-px left-6 right-0 h-px bg-gray-200" /> : null}
                                    </td>
                                    <td
                                        className={classNames(
                                            planIdx === 0 ? '' : 'border-t border-gray-200',
                                            'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                        )}
                                    >
                                        {item.id || "--"}
                                    </td>
                                    <td
                                        className={classNames(
                                            planIdx === 0 ? '' : 'border-t border-gray-200',
                                            'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                        )}
                                    >
                                        <input
                                            type="number"
                                            value={item.unitPrice}
                                            onChange={(e) => {
                                                const updated = [...inventory];
                                                updated[planIdx].unitPrice = parseFloat(e.target.value) || 0;
                                                setInventory(updated);
                                            }}
                                            className="w-full bg-transparent border-b border-gray-200 focus:outline-none"
                                        />
                                    </td>
                                    <td
                                        className={classNames(
                                            planIdx === 0 ? '' : 'border-t border-gray-200',
                                            'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                        )}
                                    >
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => {
                                                const updated = [...inventory];
                                                updated[planIdx].quantity = parseFloat(e.target.value) || 0;
                                                setInventory(updated);
                                            }}
                                            className="w-full bg-transparent border-b border-gray-200 focus:outline-none"
                                        />
                                    </td>
                                    <td
                                        className={classNames(
                                            planIdx === 0 ? '' : 'border-t border-gray-200',
                                            'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell',
                                        )}
                                    >
                                        {item.category}
                                    </td>
                                    <td
                                        className={classNames(
                                            planIdx === 0 ? '' : 'border-t border-gray-200',
                                            'px-3 py-3.5 text-sm text-gray-500',
                                        )}
                                    >
                                        <div className="sm:hidden">${numberWithCommas(((item.unitPrice || 0) * (item.quantity || 0)))}</div>
                                        <div className="hidden sm:block">${numberWithCommas(((item.unitPrice || 0) * (item.quantity || 0)))}</div>
                                    </td>
                                    <td
                                        className={classNames(
                                            planIdx === 0 ? '' : 'border-t border-transparent',
                                            'relative py-3.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 flex items-center justify-end',
                                        )}
                                    >
                                        <Button
                                            type="button"
                                            onClick={() => saveItem(item)}
                                            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-0.5 text-sm font-semibold text-white shadow-sm hover:bg-opacity-80"
                                        >
                                            Save
                                        </Button>

                                        <Button
                                            type="button"
                                            // disabled={plan.isCurrent}
                                            onClick={() => deleteItem(item.id)}
                                            className="inline-flex items-center pl-5 py-1.5 text-sm font-semibold text-gray-900 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white"
                                        >
                                            <TrashIcon className={`size-5 text-red-500 hover:stroke-2`}/>
                                        </Button>

                                        {/*{planIdx !== 0 ? <div className="absolute -top-px left-0 right-6 h-px bg-gray-200" /> : null}*/}
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>

            </div>



        </div>
    )
}

export default ProductsPage;
